import React from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Award, Printer, Trash2, Users } from 'lucide-react';

const StatusBadge = ({ status, isArabic }) => {
  const map = {
    pending: { ar: 'معلّق', en: 'Pending', cls: 'bg-amber-500/20 text-amber-400' },
    approved: { ar: 'معتمد', en: 'Approved', cls: 'bg-emerald-500/20 text-emerald-400' },
    rejected: { ar: 'مرفوض', en: 'Rejected', cls: 'bg-red-500/20 text-red-400' },
  };
  const m = map[status];
  if (!m) return null;
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] ${m.cls}`}>
      {isArabic ? m.ar : m.en}
    </span>
  );
};

const FilterBar = ({ filter, setFilter, onChange, isArabic, testidPrefix }) => (
  <div className="flex gap-1">
    {['pending', 'approved', 'rejected', 'all'].map((f) => (
      <Button
        key={f}
        size="sm"
        variant={filter === f ? 'default' : 'ghost'}
        onClick={() => {
          setFilter(f);
          onChange(f);
        }}
        data-testid={`${testidPrefix}-${f}`}
      >
        {f === 'pending' && (isArabic ? 'معلّقة' : 'Pending')}
        {f === 'approved' && (isArabic ? 'معتمدة' : 'Approved')}
        {f === 'rejected' && (isArabic ? 'مرفوضة' : 'Rejected')}
        {f === 'all' && (isArabic ? 'الكل' : 'All')}
      </Button>
    ))}
  </div>
);

const EmptyState = ({ isArabic }) => (
  <div className="text-center py-10 text-muted-foreground">
    {isArabic ? 'لا توجد طلبات' : 'No requests'}
  </div>
);

export const AwardRequestsTab = ({
  isArabic,
  requests,
  filter,
  setFilter,
  onFetch,
  onDecide,
  onDelete,
}) => (
  <Card className="overflow-hidden bg-card border-white/10" data-testid="award-requests-panel">
    <div className="p-3 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
      <h3 className="font-bold flex items-center gap-2">
        <Award className="w-4 h-4 text-emerald-500" />
        {isArabic ? 'طلبات إضافة النقاط من الحلاقين' : 'Barber-submitted point award requests'}
      </h3>
      <FilterBar filter={filter} setFilter={setFilter} onChange={onFetch} isArabic={isArabic} testidPrefix="award-filter" />
    </div>
    {requests.length === 0 ? (
      <EmptyState isArabic={isArabic} />
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40">
            <tr>
              <th className="text-start py-3 px-3">{isArabic ? 'العضو' : 'Member'}</th>
              <th className="text-start py-3 px-3">{isArabic ? 'الحلاق' : 'Barber'}</th>
              <th className="text-center py-3 px-3">{isArabic ? 'النقاط' : 'Points'}</th>
              <th className="text-start py-3 px-3">{isArabic ? 'الخدمة' : 'Service'}</th>
              <th className="text-start py-3 px-3">{isArabic ? 'السبب' : 'Reason'}</th>
              <th className="text-start py-3 px-3">{isArabic ? 'التاريخ' : 'Date'}</th>
              <th className="text-center py-3 px-3">{isArabic ? 'الحالة' : 'Status'}</th>
              <th className="text-center py-3 px-3">{isArabic ? 'إجراء' : 'Action'}</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((r) => (
              <tr key={r.id} className="border-t border-white/5">
                <td className="py-3 px-3">
                  <div className="font-medium">{r.member_name}</div>
                  <div className="text-[10px] text-muted-foreground">#{r.membership_number}</div>
                </td>
                <td className="py-3 px-3 text-xs">{r.requested_by_name}</td>
                <td className="py-3 px-3 text-center font-bold text-emerald-500">+{r.points}</td>
                <td className="py-3 px-3 text-xs">{r.service_name || '-'}</td>
                <td className="py-3 px-3 text-xs max-w-[260px]">
                  <div className="truncate" title={r.reason}>{r.reason || '-'}</div>
                  {r.rejection_reason && (
                    <div className="text-[10px] text-red-400 mt-0.5">
                      {isArabic ? 'سبب الرفض: ' : 'Rejected: '}
                      {r.rejection_reason}
                    </div>
                  )}
                </td>
                <td className="py-3 px-3 text-xs text-muted-foreground">
                  {new Date(r.requested_at).toLocaleString(isArabic ? 'ar-AE' : 'en-US')}
                </td>
                <td className="py-3 px-3 text-center">
                  <StatusBadge status={r.status} isArabic={isArabic} />
                </td>
                <td className="py-3 px-3 text-center">
                  {r.status === 'pending' ? (
                    <div className="flex gap-1 justify-center">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-emerald-500 hover:bg-emerald-500/10"
                        onClick={() => onDecide(r, true)}
                        data-testid={`award-approve-${r.id}`}
                        title={isArabic ? 'اعتماد' : 'Approve'}
                      >✓</Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:bg-red-500/10"
                        onClick={() => onDecide(r, false)}
                        data-testid={`award-reject-${r.id}`}
                        title={isArabic ? 'رفض' : 'Reject'}
                      >✕</Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-muted-foreground hover:text-red-400"
                      onClick={() => onDelete(r)}
                      title={isArabic ? 'حذف' : 'Delete'}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </Card>
);

export const PrintRequestsTab = ({
  isArabic,
  requests,
  filter,
  setFilter,
  onFetch,
  onDecide,
  onOpenReferral,
}) => (
  <Card className="overflow-hidden bg-card border-white/10" data-testid="print-requests-panel">
    <div className="p-3 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
      <h3 className="font-bold flex items-center gap-2">
        <Printer className="w-4 h-4 text-amber-500" />
        {isArabic ? 'طلبات طباعة سجلات الإحالة' : 'Referral History Print Requests'}
      </h3>
      <FilterBar filter={filter} setFilter={setFilter} onChange={onFetch} isArabic={isArabic} testidPrefix="print-filter" />
    </div>
    {requests.length === 0 ? (
      <EmptyState isArabic={isArabic} />
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40">
            <tr>
              <th className="text-start py-3 px-3">{isArabic ? 'العضو' : 'Member'}</th>
              <th className="text-start py-3 px-3">{isArabic ? 'مقدّم الطلب' : 'Requested by'}</th>
              <th className="text-start py-3 px-3">{isArabic ? 'التاريخ' : 'Date'}</th>
              <th className="text-center py-3 px-3">{isArabic ? 'الحالة' : 'Status'}</th>
              <th className="text-center py-3 px-3">{isArabic ? 'صالح حتى' : 'Valid until'}</th>
              <th className="text-center py-3 px-3">{isArabic ? 'إجراء' : 'Action'}</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((r) => (
              <tr key={r.id} className="border-t border-white/5">
                <td className="py-3 px-3">
                  <div className="font-medium">{r.member_name}</div>
                  <div className="text-[10px] text-muted-foreground">#{r.membership_number}</div>
                </td>
                <td className="py-3 px-3 text-xs">{r.requested_by_name}</td>
                <td className="py-3 px-3 text-xs text-muted-foreground">
                  {new Date(r.requested_at).toLocaleString(isArabic ? 'ar-AE' : 'en-GB')}
                </td>
                <td className="py-3 px-3 text-center">
                  <StatusBadge status={r.status} isArabic={isArabic} />
                </td>
                <td className="py-3 px-3 text-center text-[10px] text-muted-foreground">
                  {r.approved_until
                    ? new Date(r.approved_until).toLocaleString(isArabic ? 'ar-AE' : 'en-GB')
                    : '-'}
                  {r.rejection_reason && <div className="text-red-400 mt-1">{r.rejection_reason}</div>}
                </td>
                <td className="py-3 px-3 text-center">
                  {r.status === 'pending' ? (
                    <div className="flex gap-1 justify-center">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-emerald-500 hover:bg-emerald-500/10"
                        onClick={() => onDecide(r, true)}
                        data-testid={`print-approve-${r.id}`}
                      >✓</Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:bg-red-500/10"
                        onClick={() => onDecide(r, false)}
                        data-testid={`print-reject-${r.id}`}
                      >✕</Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-amber-500"
                      onClick={() => onOpenReferral(r)}
                      title={isArabic ? 'فتح سجل الإحالة' : 'Open referral history'}
                    >
                      <Users className="w-3 h-3" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </Card>
);
